import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { captureToofiAnalytics } from "@/lib/toofi-analytics";
import { cn } from "@/lib/utils";
import type { ToofiShellSidebarView } from "@/components/ui/reader-ai/types";
import { Archive, Clock3, Layers3, MessageSquareText, PanelLeftClose, PanelLeftOpen, PenSquare } from "lucide-react";

type ToofiIconRailProps = {
  currentView: ToofiShellSidebarView;
  sidebarCollapsed?: boolean;
  showArchived?: boolean;
  className?: string;
  onNewChat: () => void;
  onStartTemporaryChat?: () => void;
  onShowChats: () => void;
  onShowArtifacts: () => void;
  onToggleArchived: () => void;
  onToggleSidebar: () => void;
};

const iconButtonClass =
  "h-10 w-10 rounded-[1.05rem] border transition-all duration-200 active:scale-95 shadow-[inset_0_1px_0_rgba(255,255,255,0.72),0_10px_24px_rgba(148,163,184,0.10)]";

export function ToofiIconRail({
  currentView,
  sidebarCollapsed = false,
  showArchived = false,
  className,
  onNewChat,
  onStartTemporaryChat,
  onShowChats,
  onShowArtifacts,
  onToggleArchived,
  onToggleSidebar,
}: ToofiIconRailProps) {
  const renderAction = ({
    label,
    active = false,
    onClick,
    icon: Icon,
    iconClassName,
  }: {
    label: string;
    active?: boolean;
    onClick: () => void;
    icon: typeof PenSquare;
    iconClassName?: string;
  }) => (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={label}
          className={cn(
            iconButtonClass,
            active
              ? "border-blue-200/90 bg-blue-50/95 text-blue-700 hover:bg-blue-100/90"
              : "border-slate-200/80 bg-white/86 text-slate-500 hover:border-slate-300/80 hover:bg-white hover:text-slate-900",
          )}
          onClick={onClick}
        >
          <Icon className={cn("h-4.5 w-4.5", iconClassName)} />
        </Button>
      </TooltipTrigger>
      <TooltipContent
        side="right"
        className="rounded-full border-slate-200/80 bg-white/96 px-2.5 py-1 text-[11px] font-medium text-slate-600 shadow-[0_12px_28px_rgba(148,163,184,0.12)] backdrop-blur-xl"
      >
        {label}
      </TooltipContent>
    </Tooltip>
  );

  return (
    <TooltipProvider delayDuration={120}>
      <div
        className={cn(
          "flex h-full flex-col items-center justify-between rounded-[1.7rem] border border-white/75 bg-[linear-gradient(180deg,rgba(255,255,255,0.9),rgba(248,250,252,0.78))] px-2 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.75),0_18px_40px_rgba(148,163,184,0.10)] backdrop-blur-2xl",
          className,
        )}
      >
        <div className="flex flex-col items-center gap-2">
          {renderAction({
            label: "Start a new chat",
            onClick: () => {
              captureToofiAnalytics("toofi_icon_rail_action_clicked", { action: "new_chat" });
              onNewChat();
            },
            icon: PenSquare,
          })}
          {onStartTemporaryChat
            ? renderAction({
                label: "Start a temporary chat",
                onClick: () => {
                  captureToofiAnalytics("toofi_icon_rail_action_clicked", { action: "temporary_chat" });
                  onStartTemporaryChat();
                },
                icon: Clock3,
                iconClassName: "text-amber-500",
              })
            : null}
        </div>

        <div className="flex flex-col items-center gap-2">
          {renderAction({
            label: "Chats",
            active: currentView === "chats" && !showArchived,
            onClick: () => {
              captureToofiAnalytics("toofi_icon_rail_action_clicked", { action: "show_chats" });
              onShowChats();
            },
            icon: MessageSquareText,
          })}
          {renderAction({
            label: "Artifacts",
            active: currentView === "artifacts",
            onClick: () => {
              captureToofiAnalytics("toofi_icon_rail_action_clicked", { action: "show_artifacts" });
              onShowArtifacts();
            },
                icon: Layers3,
              })}
        </div>

        <div className="flex flex-col items-center gap-2">
          {renderAction({
            label: showArchived ? "Show active chats" : "Show archived chats",
            active: showArchived,
            onClick: () => {
              captureToofiAnalytics("toofi_icon_rail_action_clicked", {
                action: showArchived ? "show_active_chats" : "show_archived_chats",
              });
              onToggleArchived();
            },
            icon: Archive,
          })}
          {renderAction({
            label: sidebarCollapsed ? "Expand chat rail" : "Collapse chat rail",
            onClick: () => {
              captureToofiAnalytics(sidebarCollapsed ? "toofi_sidebar_expanded" : "toofi_sidebar_collapsed");
              onToggleSidebar();
            },
            icon: sidebarCollapsed ? PanelLeftOpen : PanelLeftClose,
          })}
        </div>
      </div>
    </TooltipProvider>
  );
}
